import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { CircularProgress, Typography } from "@mui/material";
import { useShallow } from "zustand/shallow";

import useNotifier from "hooks/useNotifier";
import IndexDB from "services/IndexDB";
import LocalStorage from "services/LocalStorage";
import { addArrayItem, removeArrayItem, updateArrayItem, updateArrayItems } from "utils/arrayOfObjectsOperations";
import getParamByRouteSegment from "utils/getParamByRouteSegment";
import EventApi from "api/event";
import FriendApi from "api/friend";
import GroupApi from "api/group";
import ProfileApi from "api/profile";
import StorageApi, { StorageBuckets } from "api/storage";
import VoteApi from "api/vote";
import useBoundStore from "store";
import { AugmentedGroup } from "store/appDataSlice";
import styles from "./appDataSynchronizer.module.scss";

const eventUrlRegex = /^\/group\/[0-9]*\/event\/[0-9]*$/;
const vehiclesUrlRegex = /^\/group\/[0-9]*\/event\/[0-9]*\/vehicles$/;
const groupUrlRegex = /^\/group\/[0-9]*$/;
const urlContainsGroupRegex = /^\/group\/[0-9]*/;

function AppDataSynchronizer() {
  const isAppFocused = useBoundStore((state) => state.miscellaneous.isAppFocused);
  const { auth, session } = useBoundStore(
    useShallow((state) => ({
      auth: state.user.auth,
      session: state.user.session,
    }))
  );
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const { realtimeSubscribed, setMiscellaneous } = useBoundStore(
    useShallow((state) => ({
      appStatus: state.miscellaneous.appStatus,
      realtimeSubscribed: state.miscellaneous.realtimeSubscribed,
      setMiscellaneous: state.miscellaneous.set,
    }))
  );
  const appData = useBoundStore((state) => state.appData);
  const { showNotification } = useNotifier();
  const localStorage = LocalStorage.getInstance();
  const location = useLocation();

  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);
  const friendApi = useMemo(() => new FriendApi(showNotification), [showNotification]);
  const groupApi = useMemo(() => new GroupApi(showNotification), [showNotification]);
  const profileApi = useMemo(() => new ProfileApi(showNotification), [showNotification]);
  const voteApi = useMemo(() => new VoteApi(showNotification), [showNotification]);
  const storageApi = useMemo(() => new StorageApi(), []);

  const setAvatarsFromIndexDbToState = useCallback(async () => {
    const indexDB = await IndexDB.getInstance();
    const indexDBAvatars = await indexDB.getAllSmallAvatars();
    const stateAvatars: { [key: string]: Blob } = {};
    indexDBAvatars.forEach((avatar) => {
      stateAvatars[avatar.key] = avatar.value;
    });

    setMiscellaneous({ smallAvatars: stateAvatars });
  }, [setMiscellaneous]);

  /**
   * Synchronize small avatars (remove old ones, add new ones)
   */
  const synchSmallAvatars = useCallback(
    async (groupsAvatars: string[], usersAvatars: string[]) => {
      const indexDB = await IndexDB.getInstance();
      const indexDBAvatarsKeys = await indexDB.getAllSmallAvatarsKeys();
      const allAvatarKeys: string[] = [];
      const newAvatarsKeys: string[] = [];
      const newAvatarsPromises: Promise<{
        file: Blob | null;
        error: Error | null;
      }>[] = [];

      // Add groups avatars which are not already present in indexDB
      groupsAvatars.forEach((groupAvatar) => {
        allAvatarKeys.push(groupAvatar);
        if (!indexDBAvatarsKeys.includes(groupAvatar)) {
          newAvatarsPromises.push(storageApi.getFile(`s-${groupAvatar}`, StorageBuckets.GROUPS_AVATARS));
          newAvatarsKeys.push(groupAvatar);
        }
      });
      // Add users avatars which are not already present in indexDB
      usersAvatars.forEach((userAvatar) => {
        allAvatarKeys.push(userAvatar);
        if (!indexDBAvatarsKeys.includes(userAvatar)) {
          newAvatarsPromises.push(storageApi.getFile(`s-${userAvatar}`, StorageBuckets.PROFILES_AVATARS));
          newAvatarsKeys.push(userAvatar);
        }
      });

      // Delete unused avatars
      const avatarsToBeDeleted = indexDBAvatarsKeys.filter((key) => !allAvatarKeys.includes(key));
      if (avatarsToBeDeleted.length > 0) {
        indexDB.deleteSmallAvatars(avatarsToBeDeleted);
      }

      // Add the new avatars to indexDB and update state
      const newAvatars = await Promise.all(newAvatarsPromises);

      if (newAvatars.length > 0) {
        const stateAvatars: { [key: string]: Blob } = {};
        const avatarFiles: Blob[] = [];

        newAvatars.forEach((avatar, i) => {
          if (avatar.file) {
            avatarFiles.push(avatar.file);
            stateAvatars[newAvatarsKeys[i]] = avatar.file;
          }
        });

        indexDB.addSmallAvatars(avatarFiles, newAvatarsKeys);

        setMiscellaneous({ smallAvatars: stateAvatars });
      }
    },
    [setMiscellaneous, storageApi]
  );

  /**
   * Data to be fetched when first loading the app
   */
  const fetchAppData = useCallback(async () => {
    const authUid = auth?.id;

    if (authUid && auth?.profileCreated) {
      appData.set({
        isLoading: true,
      });

      // Set groups, events and friends
      const groups = (await groupApi.getAllGroups(authUid)).groups.map((group) => ({ ...group, stale: false }));
      const groupIds = groups.map((group) => group.id);

      // Get all users ids from all groups
      let allUsersIds: Array<number> = [];
      groups.forEach((group) => {
        allUsersIds.push(...group.users);
      });
      allUsersIds = Array.from(new Set(allUsersIds));

      // Get users of all groups
      const { users: groupsUsers } = await profileApi.getUsersByIds(allUsersIds);

      const { friends } = await friendApi.getFriends(authUid);
      const { friendRequests } = await friendApi.getFriendRequests(authUid);

      // Sync small avatars
      const groupsAvatars = groups.map((group) => group.avatar).filter((avatar) => avatar) as string[];
      const groupsUsersAvatars = groupsUsers.map((user) => user.avatar);
      const friendsAvatars = friends.map((friend) => friend.avatar);
      const mergedAvatars = [...groupsUsersAvatars, ...friendsAvatars].filter((avatar) => avatar) as string[];
      const uniqueMergedAvatars = [...new Set(mergedAvatars)];
      synchSmallAvatars(groupsAvatars, uniqueMergedAvatars);

      // Get all events
      const events = (await eventApi.getAllEvents(groupIds)).events.map((event) => ({ ...event, stale: false }));

      // Get all votes
      const { votes } = await voteApi.getVotesByEventsIds(events.map((event) => event.id));

      appData.set({
        events,
        friends,
        friendRequests,
        groups,
        groupsUsers,
        initialized: true,
        isLoading: false,
        votes,
      });
    }
  }, [appData.set, eventApi, friendApi, groupApi, profileApi, auth?.id, auth?.profileCreated, synchSmallAvatars]);

  /**
   * Sync data so that the user won't have stale data when the app is in background
   * for mobile/tablet devices
   */
  const syncData = useCallback(async () => {
    const authUid = auth?.id;
    const locationPathname = location.pathname;
    let isSyncing = false;
    // This was moved outside of its if block to avoid concurrent calls because
    // of how useEffect works
    let synchedGroups: AugmentedGroup[] | undefined;

    if (authUid) {
      // Sync groups and friends requests count
      if (location.pathname === "/" && appData.staleGroupsAndFriendRequests) {
        if (!isSyncing) {
          isSyncing = true;
          appData.set({ isSyncing: true });
        }

        const res = await Promise.all([groupApi.getAllGroups(authUid), friendApi.getFriendRequests(authUid)]);

        appData.set({
          groups: res[0].groups.map((group) => ({ ...group, stale: false })),
          friendRequests: res[1].friendRequests,
          staleGroupsAndFriendRequests: false,
        });
      }

      // Sync group
      if (urlContainsGroupRegex.test(locationPathname)) {
        const groupId = parseInt(getParamByRouteSegment(locationPathname, "group"));
        const group = appData.groups.find((g) => g.id === groupId);

        if (group?.stale) {
          if (!isSyncing) {
            isSyncing = true;
            appData.set({ isSyncing: true });
          }

          const { group: updatedGroup } = await groupApi.getGroupById(groupId);
          if (updatedGroup) {
            synchedGroups = updateArrayItem({ ...updatedGroup, stale: false }, appData.groups);
          }

          // Trigger appData.set only if conditions are not met, this way
          // there won't more than one data fetching (because syncedGroups are also set for
          // when syncing event/events)
          if (!groupUrlRegex.test(locationPathname) && !eventUrlRegex.test(locationPathname)) {
            appData.set({
              ...(synchedGroups && { groups: synchedGroups }),
            });
          }
        }
      }

      // Sync event
      if (eventUrlRegex.test(locationPathname) || vehiclesUrlRegex.test(locationPathname)) {
        const eventId = parseInt(getParamByRouteSegment(locationPathname, "event"));
        const staleEvent = appData.events.find((event) => event.id === eventId && event.stale);

        if (staleEvent) {
          if (!isSyncing) {
            isSyncing = true;
            appData.set({ isSyncing: true });
          }

          const { event } = await eventApi.getEvent(eventId);
          if (event) {
            const { votes } = await voteApi.getVotesByEventsIds([event.id]);
            const updatedVotes = updateArrayItems(votes, appData.votes);

            appData.set({
              events: updateArrayItem({ ...event, stale: false }, appData.events),
              votes: updatedVotes,
              ...(synchedGroups && { groups: synchedGroups }),
            });
          }
        } else if (synchedGroups) {
          appData.set({ groups: synchedGroups });
          synchedGroups = undefined;
        }
      }

      // Sync friends
      if (appData.staleFriendsAndRequests && location.pathname === "/friends") {
        if (!isSyncing) {
          isSyncing = true;
          appData.set({ isSyncing: true });
        }
        const { friends } = await friendApi.getFriends(authUid);
        const { friendRequests } = await friendApi.getFriendRequests(authUid);

        appData.set({
          friends,
          friendRequests,
          staleFriendsAndRequests: false,
        });
      }

      // Sync events
      if (groupUrlRegex.test(locationPathname)) {
        const groupId = parseInt(getParamByRouteSegment(locationPathname, "group"));

        const staleEvents = appData.events.filter((event) => event.group_id === groupId && event.stale);

        if (staleEvents.length) {
          if (!isSyncing) {
            isSyncing = true;
            appData.set({ isSyncing: true });
          }

          const { events, error } = await eventApi.getEventsByGroup(groupId);
          const oldGroupEvents = appData.events.filter((event) => event.group_id === groupId);
          let updatedEvents = appData.events;
          // Remove old events
          oldGroupEvents.forEach((oldEvent) => {
            updatedEvents = removeArrayItem(oldEvent.id, updatedEvents);
          });

          // Update with the new ones
          const eventsIds: number[] = [];
          events.forEach((event) => {
            eventsIds.push(event.id);
            updatedEvents = addArrayItem({ ...event, stale: false }, updatedEvents);
          });

          const { votes } = await voteApi.getVotesByEventsIds(eventsIds);
          const updatedVotes = updateArrayItems(votes, appData.votes);

          if (!error) {
            appData.set({
              events: updatedEvents,
              votes: updatedVotes,
              ...(synchedGroups && { groups: synchedGroups }),
            });
          }
        } else if (synchedGroups) {
          appData.set({ groups: synchedGroups });
          synchedGroups = undefined;
        }
      }

      if (isSyncing) {
        appData.set({ isSyncing: false, isStale: false });
      }
    }
  }, [
    appData.set,
    eventApi,
    friendApi,
    groupApi,
    location.pathname,
    auth?.id,
    appData.groups,
    appData.events,
    appData.staleGroupsAndFriendRequests,
    appData.staleFriendsAndRequests,
    session?.expires_at,
  ]);

  /**
   * Initial fetch of all app data
   */
  useEffect(() => {
    if (realtimeSubscribed && !appData.initialized) {
      fetchAppData();
    }
  }, [appData.initialized, fetchAppData, realtimeSubscribed]);

  /**
   * Get avatars from indexDB and set them in state
   */
  useEffect(() => {
    setAvatarsFromIndexDbToState();
  }, [setAvatarsFromIndexDbToState]);

  /**
   * Fetch needed data when user focus tab
   */
  useEffect(() => {
    if (isAppFocused && !appData.isLoading && isMobileDevice && realtimeSubscribed) {
      syncData();
    } else if (!isAppFocused && !appData.isStale && appData.initialized) {
      // Mark app data as stale
      const events = appData.events.map((event) => ({
        ...event,
        stale: true,
      }));
      const groups = appData.groups.map((group) => ({
        ...group,
        stale: true,
      }));

      appData.set({
        ...(events.length && { events }),
        ...(groups.length && { groups }),
        isSyncing: false,
        isStale: true,
        staleGroupsAndFriendRequests: true,
        staleFriendsAndRequests: true,
      });
    }
  }, [
    appData.events,
    appData.groups,
    appData.initialized,
    appData.isLoading,
    appData.isStale,
    appData.set,
    appData.staleFriendsAndRequests,
    isAppFocused,
    isMobileDevice,
    realtimeSubscribed,
    syncData,
  ]);

  /**
   * Save app data to local storage to have it for faster loading time and
   * offline use
   */
  useEffect(() => {
    localStorage.setAppData(appData);
  }, [appData, localStorage]);

  return appData.isSyncing ? (
    <div className={styles.syncContainer}>
      <CircularProgress size={14} />
      <Typography className={styles.text} color='text.secondary' variant='caption'>
        Syncing...
      </Typography>
    </div>
  ) : null;
}

export default AppDataSynchronizer;
